import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { XMarkIcon, InformationCircleIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Fragment, ReactNode } from 'react';
import { TooltipV2 } from '../tooltip/TooltipV2';
import Button from '../button/Button';
import Spinner from '../spinner/Spinner';

const styles = {
    icon: {
        info: 'bg-indigo-100',
        success: 'bg-green-100',
        error: 'bg-red-100',
    },
};

export type DialogProps = {
    isOpen: boolean;
    title: string;
    description: string | ReactNode;
    type: 'info' | 'success' | 'error';
    primaryAction: string;
    secondaryAction?: string;
    onPrimaryActionClick?: () => void;
    onSecondaryActionClick?: () => void;
    onClose?: () => void;
    disablePrimaryAction?: boolean;
    disabledPrimaryActionHint?: string;
    children?: ReactNode | ReactNode[];
    isLoading?: boolean;
};

function Dialog({
    isOpen,
    isLoading,
    title,
    description,
    type,
    primaryAction,
    secondaryAction,
    children,
    disablePrimaryAction,
    disabledPrimaryActionHint,
    onPrimaryActionClick,
    onSecondaryActionClick,
    onClose,
}: DialogProps): JSX.Element | null {
    function handleClose(): void {
        if (!isLoading) {
            onClose();
        }
    }

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <HeadlessDialog as="div" className="fixed z-[60] inset-0 overflow-y-auto" onClose={handleClose}>
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0 relative">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <HeadlessDialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>
                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                        &#8203;
                    </span>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-100"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="relative inline-block  text-left  transform transition-all max-w-lg ">
                            {isLoading && (
                                <div className="absolute top-0 left-0 h-full w-full bg-white/[0.65] z-50 flex items-center justify-center pointer-events-none">
                                    <Spinner />
                                </div>
                            )}
                            <div className="bg-white px-4 pt-5 pb-4 rounded-tr-md rounded-tl-md">
                                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                                    <button
                                        disabled={isLoading}
                                        type="button"
                                        className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                        onClick={(): void => {
                                            onClose && onClose();
                                        }}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                <div className="sm:flex sm:items-start">
                                    <div
                                        className={`${styles.icon[type]} mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10`}
                                    >
                                        {type === 'error' && (
                                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                                        )}
                                        {type === 'info' && (
                                            <InformationCircleIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                                        )}
                                        {type === 'success' && <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />}
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <HeadlessDialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                            {title}
                                        </HeadlessDialog.Title>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">{description}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* CUSTOM CONTENT */}
                            {children && <div className="bg-white pl-[72px]">{children}</div>}

                            {/* Actions */}
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-br-md rounded-bl-md">
                                <span className="sm:ml-3 ml-0 w-full inline-flex justify-center sm:w-auto ">
                                    <TooltipV2
                                        message={disabledPrimaryActionHint}
                                        disabled={!disablePrimaryAction || !disabledPrimaryActionHint}
                                    >
                                        <Button
                                            fill
                                            label={primaryAction}
                                            type={type === 'error' ? 'danger' : 'primary'}
                                            onClick={(): void => {
                                                onPrimaryActionClick && onPrimaryActionClick();
                                            }}
                                            disabled={isLoading || disablePrimaryAction}
                                        />
                                    </TooltipV2>
                                </span>
                                {secondaryAction && (
                                    <span className="mt-3 sm:mt-0 w-full inline-flex justify-center sm:w-auto">
                                        <Button
                                            fill
                                            type="secondary"
                                            label={secondaryAction}
                                            onClick={(): void => {
                                                onSecondaryActionClick && onSecondaryActionClick();
                                            }}
                                            disabled={isLoading}
                                        />
                                    </span>
                                )}
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </HeadlessDialog>
        </Transition.Root>
    );
}

export default Dialog;
